import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { getDefaultTranslationKey } from 'customer-data-filters-ui/utils/filterFamilyTranslation';
import get from 'transmute/get';
import { getDynamicFilterFamilyBasis, parseDynamicFilterFamilyId, testDynamicFilterFamily } from 'customer-data-filters-ui/filterQueryFormat/DynamicFilterFamilies';
import { AppObjectTypeIdRegex, CustomObjectTypeIdRegex, isCustomBehavioralEventObjectTypeId, isIntegratorEventObjectTypeId, isUnifiedEventObjectTypeId, ObjectTypeIdRegex } from 'customer-data-filters-ui/filterQueryFormat/ObjectTypeId';
import getIn from 'transmute/getIn';
import { getObjectName, getIntegrationName, getUnifiedEventCategoryGroupName, getUnifiedEventName, getCustomBehavioralEventName, getIntegratorEventName } from 'customer-data-filters-ui/strategy/filterFamilyEntityNameGetters';
import { objectHasCustomLabels } from 'customer-data-filters-ui/utils/filterFamilyTranslationConfig';
import { FilterFamilyObjectTypeIdTranslator, LegacyFilterFamilyObjectNameTranslator } from 'customer-data-filters-ui/components/common/FilterFamilyTranslator';
import { HUBSPOT_DEFINED } from '../filterQueryFormat/associations/AssociationCategory';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import I18n from 'I18n';
import { SMS_SUBSCRIPTION_ACCESS, WHATSAPP_SUBSCRIPTION_ACCESS } from '../permissions/scopes';
import { ASSOCIATION_DIRECTION, getId, getInverseId, getInverseLabel, getLabel } from '../filterQueryFormat/associations/AssociationUtilities';
import { jsx as _jsx } from "react/jsx-runtime";
export const isWildcardAssociation = association => {
  return association.hasAllAssociatedObjects === true;
};

/**
 * Check whether given association is a public "Primary" association
 */
export const isPublicPrimaryAssociation = association => association.isPrimary;
export const isInversePrimaryAssociation = association => association.isInversePrimary;

/**
 * Check whether given association should not be displayed (is not customer-facing)
 * TODO: remove once these associations no longer exist
 */
const isHiddenAssociation = association => {
  return association.category === HUBSPOT_DEFINED && Number(association.id) >= 33 && Number(association.id) <= 46;
};
export const isLabeledAssociation = (association, associationDirection = ASSOCIATION_DIRECTION.STANDARD) => {
  return associationDirection === ASSOCIATION_DIRECTION.STANDARD ? !!association.label : !!association.inverseLabel;
};
export const makeGetFilterFamilyTranslationKey = (baseFilterFamily, isSameObjectAssociation, associationId, data) => {
  return filterFamily => {
    if (filterFamily === baseFilterFamily) {
      if (isSameObjectAssociation) {
        return 'CRM_OBJECT_SELF_ASSOCIATED';
      }
      return 'CRM_OBJECT_SELF';
    }
    const associationDefinitions = get(DSAssetFamilies.ASSOCIATION_DEFINITION, data);
    if (associationId && associationDefinitions) {
      const objectPairAssociations = associationDefinitions.getIn([baseFilterFamily, filterFamily]);
      const association = objectPairAssociations ? objectPairAssociations.find(assn => assn.id === associationId) : undefined;
      if (association) {
        if (isPublicPrimaryAssociation(association)) {
          return 'CRM_OBJECT_PRIMARY';
        }
        return 'CRM_OBJECT_ANY';
      }
      return 'CRM_OBJECT';
    }
    return getDefaultTranslationKey(filterFamily);
  };
};
export const getFetchedObjectDefinitions = (filterFamily, data) => {
  const isCustomObject = CustomObjectTypeIdRegex.test(filterFamily);
  const isAppObject = AppObjectTypeIdRegex.test(filterFamily);
  if (isCustomObject) {
    return data.get(DSAssetFamilies.CUSTOM_OBJECT_DEFINITION);
  }
  if (isAppObject) {
    return data.get(DSAssetFamilies.APP_OBJECT_DEFINITION);
  }
  return data.get(DSAssetFamilies.STANDARD_OBJECT_DEFINITION);
};
export const makeGetFilterFamilyEntityName = ({
  data
}) => {
  return (filterFamily, isPlural) => {
    if (isUnifiedEventObjectTypeId(filterFamily)) {
      return getUnifiedEventName(filterFamily, data.get(DSAssetFamilies.UNIFIED_EVENT));
    }
    if (isCustomBehavioralEventObjectTypeId(filterFamily)) {
      return getCustomBehavioralEventName(filterFamily, data.get(DSAssetFamilies.CUSTOM_BEHAVIORAL_EVENT));
    }
    if (isIntegratorEventObjectTypeId(filterFamily)) {
      return getIntegratorEventName(filterFamily, data.get(DSAssetFamilies.INTEGRATOR_EVENT));
    }
    if (ObjectTypeIdRegex.test(filterFamily)) {
      if (objectHasCustomLabels(filterFamily)) {
        return FilterFamilyObjectTypeIdTranslator(filterFamily);
      }
      const objectDefinitions = getFetchedObjectDefinitions(filterFamily, data);
      return getObjectName({
        objectTypeId: filterFamily,
        objectDefinitions,
        isPlural
      });
    }
    if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, filterFamily)) {
      return getIntegrationName(filterFamily, data.get(DSAssetFamilies.INTEGRATION));
    }
    if (testDynamicFilterFamily(DSAssetFamilies.UNIFIED_EVENT, filterFamily)) {
      return getUnifiedEventCategoryGroupName(filterFamily, data.get(DSAssetFamilies.UNIFIED_EVENT));
    }
    return LegacyFilterFamilyObjectNameTranslator(filterFamily);
  };
};
export const fetchDynamicIntegrationsSettings = fetchMore => {
  return fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.INTEGRATION
    }
  });
};
export const selectedFiltersFetches = {
  fetchDataForSelectedCTAVersion: (fetchMore, idSet) => {
    return fetchMore({
      variables: {
        assetFamily: DSAssetFamilies.CTA_TO_PLACEMENT,
        ids: idSet.toArray()
      }
    }).then(result => {
      const ctaIds = result.map(getIn(['metadata', 'placementGuid']));
      return fetchMore({
        variables: {
          assetFamily: DSAssetFamilies.CTA,
          ids: ctaIds.toArray()
        }
      });
    });
  },
  fetchDataForSelectedNonDynamicIntegrationFilters: (fetchMore, requiredIdsByAssetFamily, shouldFetchAllFilterFamilyData) => {
    const requiredNonIntegrationIdsByAssetFamily = requiredIdsByAssetFamily.delete(DSAssetFamilies.INTEGRATION).filter(idSet => shouldFetchAllFilterFamilyData ? true : idSet.size);
    return Promise.all(requiredNonIntegrationIdsByAssetFamily.map((idSet, assetFamily) => {
      if (assetFamily === DSAssetFamilies.CTA_TO_PLACEMENT) {
        return selectedFiltersFetches.fetchDataForSelectedCTAVersion(fetchMore, idSet);
      }
      return fetchMore({
        variables: {
          assetFamily,
          ids: shouldFetchAllFilterFamilyData ? undefined : idSet.toArray()
        }
      });
    }).valueSeq().toArray());
  },
  fetchDataForSelectedDynamicIntegrationFilters: (fetchMore, requiredIdsByAssetFamily) => {
    const integrationFamilies = requiredIdsByAssetFamily.get(DSAssetFamilies.INTEGRATION);
    if (!integrationFamilies) {
      return Promise.resolve();
    }
    return fetchDynamicIntegrationsSettings(fetchMore).then(results => {
      const activeIntegrationIds = integrationFamilies.keySeq().map(value => parseDynamicFilterFamilyId(DSAssetFamilies.INTEGRATION, value)).filter(id => results.get(id)).toArray();
      if (activeIntegrationIds.length > 0) {
        return fetchMore({
          variables: {
            assetFamily: DSAssetFamilies.INTEGRATION_PROPERTY,
            ids: activeIntegrationIds
          }
        });
      }
      return Promise.resolve();
    });
  }
};
export const fetchDataForSelectedFilters = (fetchMore, requiredIdsByAssetFamily, shouldFetchAllFilterFamilyData = false) => {
  return Promise.all([selectedFiltersFetches.fetchDataForSelectedNonDynamicIntegrationFilters(fetchMore, requiredIdsByAssetFamily, shouldFetchAllFilterFamilyData), selectedFiltersFetches.fetchDataForSelectedDynamicIntegrationFilters(fetchMore, requiredIdsByAssetFamily,
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 3.
  shouldFetchAllFilterFamilyData)]);
};
export const fetchBuiltInIntegrationsSettings = fetchMore => {
  return Promise.all([fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.SURVEY_MONKEY_SETTING,
      batchUpdate: true
    }
  }), fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.GOTO_WEBINAR_SETTING,
      batchUpdate: true
    }
  })]);
};
export const fetchCustomObjectDefinitions = fetchMore => {
  return fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.CUSTOM_OBJECT_DEFINITION
    }
  });
};
export const fetchAppObjectDefinitions = fetchMore => {
  return fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.APP_OBJECT_DEFINITION
    }
  });
};
export const fetchStandardObjectDefinitions = fetchMore => {
  return fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.STANDARD_OBJECT_DEFINITION
    }
  });
};
export const fetchCRMObjectDefinition = (fetchMore, objectTypeId) => {
  const assetFamily = CustomObjectTypeIdRegex.test(objectTypeId) ? DSAssetFamilies.CUSTOM_OBJECT_DEFINITION : AppObjectTypeIdRegex.test(objectTypeId) ? DSAssetFamilies.APP_OBJECT_DEFINITION : DSAssetFamilies.STANDARD_OBJECT_DEFINITION;
  return fetchMore({
    variables: {
      assetFamily,
      ids: [objectTypeId]
    }
  });
};
export const fetchAssociationDefinitions = (fetchMore, baseFilterFamily) => {
  const objectTypeIdsToFetchAssociationsFor = baseFilterFamily === DSAssetFamilies.CONTACT_PROPERTY ? [baseFilterFamily, DSAssetFamilies.DEAL_PROPERTY] : [baseFilterFamily];
  return fetchMore({
    variables: {
      assetFamily: DSAssetFamilies.ASSOCIATION_DEFINITION,
      ids: objectTypeIdsToFetchAssociationsFor
    }
  });
};
const getHasFilterFamilyAccessMap = {
  [DSAssetFamilies.WORKFLOW]: hasScope => hasScope('workflows-access-public-api'),
  [DSAssetFamilies.IN_LIST]: hasScope => hasScope('ils-lists-read'),
  [DSAssetFamilies.IMPORT]: hasScope => hasScope('crm-access') || hasScope('contacts-read'),
  [DSAssetFamilies.CTA]: hasScope => hasScope('cta-access') || hasScope('content-core-api-access'),
  [DSAssetFamilies.CTA_TO_PLACEMENT]: hasScope => hasScope('cta-access') || hasScope('content-core-api-access'),
  [DSAssetFamilies.EMAIL_CAMPAIGN]: hasScope => hasScope('content-core-api-access') || hasScope('content-editor-access'),
  [DSAssetFamilies.EVENT]: hasScope => hasScope('event-analytics-api-access'),
  [DSAssetFamilies.FORM]: hasScope => hasScope('form-submissions') || hasScope('forms-read') || hasScope('forms-access'),
  [DSAssetFamilies.PAGE_VIEW]: hasScope => hasScope('reports-page-view-filtering-access'),
  [DSAssetFamilies.CUSTOM_BEHAVIORAL_EVENT]: (hasScope, getIsUngated) => getIsUngated('Permissions:CustomEvents') ? hasScope('custom-events-view') : hasScope('behavioral-events-read'),
  [DSAssetFamilies.UNIFIED_EVENT]: hasScope => hasScope('schemas-metadata-read'),
  [DSAssetFamilies.SMS_SUBSCRIPTION]: hasScope => hasScope(SMS_SUBSCRIPTION_ACCESS),
  [DSAssetFamilies.WHATSAPP_SUBSCRIPTION]: hasScope => hasScope(WHATSAPP_SUBSCRIPTION_ACCESS)
};
export const getHasFilterFamilyAccess = (filterFamily, hasScope, getIsUngated) => {
  const hasFilterFamilyAccess = getHasFilterFamilyAccessMap[getDynamicFilterFamilyBasis(filterFamily) || filterFamily];
  if (hasFilterFamilyAccess) {
    return hasFilterFamilyAccess(hasScope, getIsUngated);
  }
  return true;
};
export const fetchFrameworkBuilderUnifiedEvents = ({
  fetchMore,
  hasScope,
  getIsUngated
}) => {
  if (getHasFilterFamilyAccess(DSAssetFamilies.UNIFIED_EVENT, hasScope, getIsUngated)) {
    return fetchMore({
      variables: {
        assetFamily: DSAssetFamilies.UNIFIED_EVENT
      }
    });
  }
  return Promise.resolve(false);
};
export const fetchCustomBehavioralEvents = ({
  fetchMore,
  getIsUngated,
  hasScope
}) => {
  if (getHasFilterFamilyAccess(DSAssetFamilies.CUSTOM_BEHAVIORAL_EVENT, hasScope, getIsUngated)) {
    return fetchMore({
      variables: {
        assetFamily: DSAssetFamilies.CUSTOM_BEHAVIORAL_EVENT
      }
    });
  }
  return Promise.resolve(false);
};
export const fetchIntegratorEvents = ({
  fetchMore,
  getIsUngated,
  hasScope
}) => {
  if (getHasFilterFamilyAccess(DSAssetFamilies.INTEGRATOR_EVENT, hasScope, getIsUngated)) {
    return fetchMore({
      variables: {
        assetFamily: DSAssetFamilies.INTEGRATOR_EVENT
      }
    });
  }
  return Promise.resolve(false);
};
export const makeAssociationOption = (id, category) => {
  return `${id}--${category}`;
};
export const convertAssnDefinitionToOption = (assn, objectName, associationDirection = ASSOCIATION_DIRECTION.STANDARD) => {
  const id = getId(assn, associationDirection);
  const inverseId = getInverseId(assn, associationDirection);
  const value = makeAssociationOption(id, assn.category);
  const isPublicPrimary = associationDirection === ASSOCIATION_DIRECTION.STANDARD ? !!isPublicPrimaryAssociation(assn) : !!isInversePrimaryAssociation(assn);
  const isWildcard = isWildcardAssociation(assn);
  const option = {
    associatedObjectName: objectName,
    id,
    inverseId,
    isPublicPrimary,
    isWildcard,
    value
  };
  if (isPublicPrimary) {
    const text = I18n.text('customerDataFiltersUI.customerDataFilters.FilterFamilyGroupHeadingTranslator.CRM_OBJECT_PRIMARY', {
      entityName: objectName
    });
    return Object.assign({}, option, {
      text
    });
  }
  if (isWildcard) {
    const text = I18n.text('customerDataFiltersUI.customerDataFilters.FilterFamilyGroupHeadingTranslator.CRM_OBJECT_ANY', {
      entityName: objectName
    });
    return Object.assign({}, option, {
      text
    });
  }
  const label = getLabel(assn, associationDirection);
  const inverseLabel = getInverseLabel(assn, associationDirection);
  const isAsymmetric = inverseLabel && label !== inverseLabel;
  if (isAsymmetric) {
    if (!label) {
      const text = I18n.text('customerDataFiltersUI.customerDataFilters.FilterFamilyGroupHeadingAssociation.onlyHasInverseLabel', {
        inverseAssociationLabel: inverseLabel
      });
      return Object.assign({}, option, {
        text
      });
    }
    const help = /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "customerDataFiltersUI.customerDataFilters.FilterFamilyGroupHeadingAssociation.pairedWithInverseLabel",
      options: {
        inverseAssociationLabel: inverseLabel
      }
    });
    return Object.assign({}, option, {
      text: label,
      inverseText: inverseLabel,
      help
    });
  }
  const text = I18n.text('customerDataFiltersUI.customerDataFilters.FilterFamilyGroupHeadingAssociation.unlabeled');
  return Object.assign({}, option, {
    text: label || text
  });
};
export const getObjectAssociationDefinitions = (baseFilterFamily, assetFamily, data) => {
  const associations = getIn([DSAssetFamilies.ASSOCIATION_DEFINITION, baseFilterFamily, assetFamily], data);
  if (associations) {
    return associations.valueSeq().toArray();
  }
  return undefined;
};
export function getFilterFamilyObjectName(filterFamily, isPlural, data) {
  const getEntityName = makeGetFilterFamilyEntityName({
    data
  });
  return getEntityName(filterFamily, isPlural);
}
export const getObjectAssociationOptions = (assetFamily, baseFilterFamily, data, associationDirection = ASSOCIATION_DIRECTION.STANDARD) => {
  const objectName = associationDirection === ASSOCIATION_DIRECTION.STANDARD ? getFilterFamilyObjectName(assetFamily, false, data) : getFilterFamilyObjectName(baseFilterFamily, false, data);
  const associations = getObjectAssociationDefinitions(baseFilterFamily, assetFamily, data);

  // if associations is `undefined` it means they aren't loaded yet
  // return `undefined` instead of `[]` to indicate that data is not loaded yet
  if (!associations) {
    return undefined;
  }
  const options = [];
  const customAssociationsGroup = {
    text: I18n.text('customerDataFiltersUI.customerDataFilters.FilterFamilyGroupHeadingAssociation.labeledOptionsGroupTitle'),
    options: []
  };
  for (const assn of associations) {
    if (isPublicPrimaryAssociation(assn) || isWildcardAssociation(assn) || isInversePrimaryAssociation(assn)) {
      options.push(convertAssnDefinitionToOption(assn, objectName, associationDirection));
    } else if (!isHiddenAssociation(assn) && isLabeledAssociation(assn, associationDirection)) {
      customAssociationsGroup.options.push(convertAssnDefinitionToOption(assn, objectName, associationDirection));
    }
  }
  if (customAssociationsGroup.options.length) {
    options.push(customAssociationsGroup);
  }
  return options;
};
export const getHasAssociationOptions = (baseFilterFamily, assetFamily, data, associationDirection = ASSOCIATION_DIRECTION.STANDARD) => {
  const associations = getObjectAssociationDefinitions(baseFilterFamily, assetFamily, data);
  return !!associations && associations.some(assn => isPublicPrimaryAssociation(assn) || isWildcardAssociation(assn) || isInversePrimaryAssociation(assn) || isLabeledAssociation(assn, associationDirection) && !isHiddenAssociation(assn));
};